import { z } from 'zod';

import type { BinaryAnswer } from '@/constants/answers';
import { EddCategory, ProhibitedCategory, type ScreeningFrequency } from '@/constants/institution';
import i18n from '@/lib/i18n';
import {
  type InstitutionDetailsV2KycCdd,
  institutionDetailsV2CddPolicyDocumentSchema,
  institutionDetailsV2KycCddAnswersSchema,
  institutionDetailsV2KycCddSchema,
  institutionDetailsV2ScreeningFrequencySchema,
} from '@/services/api/profile';
import { addFieldIssue } from '@/utils/field-issues';

export type InstitutionKycCdd = {
  cddPolicy?: BinaryAnswer;
  cddPolicyDocument?: string;
  verifiesCustomerIdentity?: BinaryAnswer;
  knowsExpectedActivity?: BinaryAnswer;
  retainsOwnershipDocuments?: BinaryAnswer;
  verifiesNatureAndPurpose?: BinaryAnswer;
  verifiesSourceOfFunds?: BinaryAnswer;
  identifiesUbos?: BinaryAnswer;
  verifiesRelatedParties?: BinaryAnswer;
  assignsRiskClassification?: BinaryAnswer;
  screensCustomers?: BinaryAnswer;
  screeningFrequency?: ScreeningFrequency;
  riskBasedPepScreening?: BinaryAnswer;
  hasKycReviewProcess?: BinaryAnswer;
  eddCategories: EddCategory[];
  eddCategoriesOther: string;
  prohibitedCategories: ProhibitedCategory[];
  prohibitedCategoriesOther: string;
};

export type InstitutionKycCddOrDefaults = InstitutionKycCdd | Partial<InstitutionKycCdd>;

export const INSTITUTION_KYC_CDD_DEFAULTS: InstitutionKycCdd = {
  eddCategories: [],
  eddCategoriesOther: '',
  prohibitedCategories: [],
  prohibitedCategoriesOther: '',
};

const institutionKycCddPolicySchema = z.discriminatedUnion(
  'cddPolicy',
  [
    z.object({
      cddPolicy: z.literal('NO'),
    }),
    z.object({
      cddPolicy: z.literal('YES'),
      cddPolicyDocument: institutionDetailsV2CddPolicyDocumentSchema,
    }),
  ],
  { error: i18n.t('institutionDetails.kycCdd.errors.cddPolicy') },
);

const institutionKycCddScreeningSchema = z
  .discriminatedUnion(
    'screensCustomers',
    [
      z.object({
        screensCustomers: z.literal('NO'),
      }),
      z.object({
        screensCustomers: z.literal('YES'),
        screeningFrequency: institutionDetailsV2ScreeningFrequencySchema,
      }),
    ],
    { error: i18n.t('institutionDetails.kycCdd.errors.screensCustomers') },
  )
  .and(institutionDetailsV2KycCddAnswersSchema.pick({ riskBasedPepScreening: true, hasKycReviewProcess: true }));

export const institutionKycCddDueDiligenceSchema = institutionKycCddPolicySchema
  .and(
    institutionDetailsV2KycCddAnswersSchema.pick({
      verifiesCustomerIdentity: true,
      knowsExpectedActivity: true,
      retainsOwnershipDocuments: true,
      verifiesNatureAndPurpose: true,
      verifiesSourceOfFunds: true,
      identifiesUbos: true,
      verifiesRelatedParties: true,
      assignsRiskClassification: true,
    }),
  )
  .and(institutionKycCddScreeningSchema);

export const institutionKycCddCategoriesSchema = institutionDetailsV2KycCddAnswersSchema
  .pick({ eddCategories: true, prohibitedCategories: true })
  .extend({ eddCategoriesOther: z.string(), prohibitedCategoriesOther: z.string() })
  .superRefine((categories, context) => {
    if (categories.eddCategories.includes(EddCategory.OTHER)) {
      addFieldIssue(context, ['eddCategoriesOther'], institutionDetailsV2KycCddAnswersSchema.shape.eddCategoriesOther.unwrap(), categories.eddCategoriesOther);
    }

    if (categories.prohibitedCategories.includes(ProhibitedCategory.OTHER)) {
      addFieldIssue(context, ['prohibitedCategoriesOther'], institutionDetailsV2KycCddAnswersSchema.shape.prohibitedCategoriesOther.unwrap(), categories.prohibitedCategoriesOther);
    }
  });

// Parsing drops a policy document or screening frequency left over from an earlier Yes; an "Other" description is only kept while Other is selected
export const institutionKycCddToInstitutionDetailsKycCddApi = ({
  eddCategoriesOther,
  prohibitedCategoriesOther,
  ...kycCdd
}: InstitutionKycCdd): Promise<InstitutionDetailsV2KycCdd> =>
  institutionDetailsV2KycCddSchema.parseAsync({
    ...kycCdd,
    ...(kycCdd.eddCategories.includes(EddCategory.OTHER) && { eddCategoriesOther }),
    ...(kycCdd.prohibitedCategories.includes(ProhibitedCategory.OTHER) && { prohibitedCategoriesOther }),
  });

export const institutionDetailsKycCddApiToInstitutionKycCdd = (kycCdd: InstitutionDetailsV2KycCdd): InstitutionKycCdd => ({
  ...kycCdd,
  eddCategoriesOther: kycCdd.eddCategoriesOther ?? '',
  prohibitedCategoriesOther: kycCdd.prohibitedCategoriesOther ?? '',
});
